// /src/pages/auth/OnboardingPage.jsx

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Activity, ChevronRight, ChevronLeft, Check,
  User, Calendar, FileText, CreditCard,
  Tag, Loader2, CheckCircle, AlertCircle, X
} from 'lucide-react';
import { useAuth, DEFAULT_ACL_CARE } from '../../contexts/AuthContext';
import { Button, Input } from '../../components/ui';
import { api } from '../../services/api';
import { needsPaymentStep } from '../../utils/access';

// Sex options
const SEX_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

// Competition level options
const COMPETITION_LEVELS = [
  { value: 'recreational', label: 'Recreational' },
  { value: 'high_school', label: 'High School' },
  { value: 'club', label: 'Club' },
  { value: 'college', label: 'College' },
  { value: 'professional', label: 'Professional' },
  { value: 'na', label: 'N/A' },
];

// Surgical graft type options
const GRAFT_TYPES = [
  { value: 'patellar_tendon_autograft', label: 'Patellar tendon autograft' },
  { value: 'hamstring_tendon_autograft', label: 'Hamstring tendon autograft' },
  { value: 'quadriceps_tendon_autograft', label: 'Quadriceps tendon autograft' },
  { value: 'allograft', label: 'Allograft (donor tissue)' },
  { value: 'other', label: 'Other' },
  { value: 'not_sure', label: 'Not sure' },
];

// Prior ACL tear options
const PRIOR_ACL_TEAR_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'left_leg', label: 'Left leg' },
  { value: 'right_leg', label: 'Right leg' },
  { value: 'both_legs', label: 'Both legs' },
];

// Sentinel date (entered as 09/09/9999) meaning "not applicable" (client convention)
const NO_SURGERY_DATE = '9999-09-09';
const NO_INJURY_DATE = '9999-09-09';

// Price constant
const PROGRAM_PRICE = 300;

export function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser, refreshUser } = useAuth();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Determine user type and payment requirement
  const isPatient = user?.patient_id !== null && user?.patient_id !== undefined;
  const isOrgLinked = !!(user?.organization_id || user?.patient?.organization_id);
  const orgName = user?.organization?.name || '';
  const isLegacyUser = user?.acl_care?.legacy_user === true;
  const showPaymentStep = needsPaymentStep(user) && !isLegacyUser;

  // Legacy user banner state
  const [showLegacyBanner, setShowLegacyBanner] = useState(true);

  // Calculate total steps based on user type
  const getTotalSteps = () => {
    if (!isPatient) return 2; // Provider: Personal Info, Terms
    if (showPaymentStep) return 4; // Patient (no org): Personal Info, Injury, Terms, Payment
    return 3; // Patient (org-linked): Personal Info, Injury, Terms
  };
  const totalSteps = getTotalSteps();

  // Form data for personal info (shared between patient and provider)
  const [personalInfo, setPersonalInfo] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone?.replace(/\D/g, '') || '',
    birthday: user?.birthday || user?.patient?.birthday || '',
    sms_preferences: user?.sms_preferences ?? true,
    email_preferences: user?.email_preferences ?? true,
  });

  // ZIP code (patients only)
  const [zip, setZip] = useState(user?.patient?.address?.zip || '');

  // Athlete profile (patients only)
  const [athleteProfile, setAthleteProfile] = useState({
    sex: user?.acl_care?.sex || '',
    height_feet: user?.acl_care?.height_feet ?? '',
    height_inches: user?.acl_care?.height_inches ?? '',
    weight_lbs: user?.acl_care?.weight_lbs ?? '',
    primary_sport: user?.acl_care?.primary_sport || '',
    competition_level: user?.acl_care?.competition_level || '',
  });

  // Injury details (patients only)
  const [injuryDetails, setInjuryDetails] = useState({
    injury_side: user?.acl_care?.injury_side || '',
    injury_date: user?.acl_care?.injury_date || '',
    surgery_date: user?.acl_care?.surgery_date || '',
    graft_type: user?.acl_care?.graft_type || '',
    pt_visits_completed: user?.acl_care?.pt_visits_completed ?? '',
    prior_acl_tear: user?.acl_care?.prior_acl_tear || '',
  });

  // Terms acceptance
  const [terms, setTerms] = useState({
    general_accepted: false,
    medical_certification_accepted: false,
    org_data_sharing_accepted: false,
  });

  // Payment step state
  const [accessCode, setAccessCode] = useState('');
  const [codeValidating, setCodeValidating] = useState(false);
  const [codeResult, setCodeResult] = useState(null); // { valid, type, percent?, stripe_coupon_id?, add_org?, org_name? }
  const [codeError, setCodeError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [paymentOrgConsent, setPaymentOrgConsent] = useState(false); // For add_org codes

  // Check for payment success return
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      // Refresh user to get updated access status
      refreshUser().then(() => {
        navigate('/program', { replace: true });
      });
    } else if (paymentStatus === 'cancelled') {
      setError('Payment was cancelled. You can try again or start a free trial.');
    }
  }, [searchParams, refreshUser, navigate]);

  // Update personal info when user data loads
  useEffect(() => {
    if (user) {
      setPersonalInfo({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone?.replace(/\D/g, '') || '',
        birthday: user.birthday || user.patient?.birthday || '',
        sms_preferences: user.sms_preferences ?? true,
        email_preferences: user.email_preferences ?? true,
      });

      if (user.patient?.address?.zip) {
        setZip(user.patient.address.zip);
      }

      if (user.acl_care) {
        setAthleteProfile({
          sex: user.acl_care.sex || '',
          height_feet: user.acl_care.height_feet ?? '',
          height_inches: user.acl_care.height_inches ?? '',
          weight_lbs: user.acl_care.weight_lbs ?? '',
          primary_sport: user.acl_care.primary_sport || '',
          competition_level: user.acl_care.competition_level || '',
        });
        setInjuryDetails({
          injury_side: user.acl_care.injury_side || '',
          injury_date: user.acl_care.injury_date || '',
          surgery_date: user.acl_care.surgery_date || '',
          graft_type: user.acl_care.graft_type || '',
          pt_visits_completed: user.acl_care.pt_visits_completed ?? '',
          prior_acl_tear: user.acl_care.prior_acl_tear || '',
        });
      }
    }
  }, [user]);

  const updatePersonalInfo = (field, value) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const updateAthleteProfile = (field, value) => {
    setAthleteProfile(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const updateInjuryDetails = (field, value) => {
    setInjuryDetails(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    updatePersonalInfo('phone', digits);
  };

  // Validation functions
  const validatePersonalInfo = () => {
    if (!personalInfo.first_name.trim()) {
      setError('First name is required');
      return false;
    }
    if (!personalInfo.last_name.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!personalInfo.phone || personalInfo.phone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    if (!personalInfo.birthday) {
      setError('Birthday is required');
      return false;
    }

    if (isPatient) {
      // ZIP code is optional

      if (!athleteProfile.sex) {
        setError('Please select your sex');
        return false;
      }
      if (athleteProfile.height_feet === '' || athleteProfile.height_inches === '') {
        setError('Please enter your height');
        return false;
      }
      if (athleteProfile.weight_lbs === '' || Number(athleteProfile.weight_lbs) <= 0) {
        setError('Please enter your weight');
        return false;
      }
      if (!athleteProfile.primary_sport.trim()) {
        setError('Please enter your primary sport (or N/A if no sport)');
        return false;
      }
      if (!athleteProfile.competition_level) {
        setError('Please select your competition level');
        return false;
      }
    }

    return true;
  };

  const validateInjuryDetails = () => {
    if (!injuryDetails.injury_side) {
      setError('Please select which knee was injured');
      return false;
    }
    if (!injuryDetails.injury_date) {
      setError('Date of ACL injury is required. Enter 09/09/9999 if you have not had an ACL injury.');
      return false;
    }
    if (!injuryDetails.surgery_date) {
      setError('Date of surgery is required. Enter 09/09/9999 if you have not had surgery.');
      return false;
    }

    const hadSurgery = injuryDetails.surgery_date !== NO_SURGERY_DATE;
    if (hadSurgery && !injuryDetails.graft_type) {
      setError('Please select your surgical graft type');
      return false;
    }
    if (hadSurgery && injuryDetails.pt_visits_completed === '') {
      setError('Please select your estimated number of post-operative physical therapy visits');
      return false;
    }

    if (!injuryDetails.prior_acl_tear) {
      setError('Please answer whether you have had a prior ACL tear');
      return false;
    }
    return true;
  };

  const validateTerms = () => {
    if (!terms.general_accepted) {
      setError('You must accept the Terms of Use to continue');
      return false;
    }
    if (isPatient && !terms.medical_certification_accepted) {
      setError('You must certify the medical statement to continue');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError('');

    if (step === 1 && !validatePersonalInfo()) return;
    if (isPatient && step === 2 && !validateInjuryDetails()) return;

    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setStep(prev => prev - 1);
  };

  // Save user data (called before payment step or on final completion)
  // markComplete: whether to set acl_onboarding_completed = true
  const saveUserData = async (markComplete = true) => {
    const now = new Date().toISOString();

    const userData = {
      first_name: personalInfo.first_name.trim(),
      last_name: personalInfo.last_name.trim(),
      phone: personalInfo.phone,
      birthday: personalInfo.birthday,
      sms_preferences: personalInfo.sms_preferences,
      email_preferences: personalInfo.email_preferences,
      terms: {
        ...user?.terms,
        acl_care: {
          accepted: true,
          accepted_at: now,
        },
      },
    };

    const aclCareData = {
      ...user?.acl_care,
    };

    // Only mark onboarding complete if specified
    if (markComplete) {
      aclCareData.acl_onboarding_completed = true;
      aclCareData.acl_onboarding_completed_at = now;
    }

    // Grant paid access to legacy users
    if (isLegacyUser && markComplete) {
      aclCareData.access_status = 'paid';
      aclCareData.access_granted_at = now;
    }

    if (!aclCareData.current_phase) {
      aclCareData.current_phase = 1;
      aclCareData.phases = {
        ...DEFAULT_ACL_CARE.phases,
        ...(aclCareData.phases || {}),
        phase_1: {
          ...DEFAULT_ACL_CARE.phases.phase_1,
          ...(aclCareData.phases?.phase_1 || {}),
          unlocked: true,
          start_date: isPatient && injuryDetails.injury_date && injuryDetails.injury_date !== NO_INJURY_DATE
            ? injuryDetails.injury_date
            : new Date().toISOString().split('T')[0],
        },
      };
    }

    if (isPatient) {
      aclCareData.injury_side = injuryDetails.injury_side;
      aclCareData.injury_date = injuryDetails.injury_date || null;
      aclCareData.surgery_date = injuryDetails.surgery_date || null;
      aclCareData.graft_type = injuryDetails.graft_type || null;
      aclCareData.pt_visits_completed = injuryDetails.pt_visits_completed !== ''
        ? Number(injuryDetails.pt_visits_completed)
        : null;
      aclCareData.prior_acl_tear = injuryDetails.prior_acl_tear || null;
      aclCareData.sex = athleteProfile.sex || null;
      aclCareData.height_feet = athleteProfile.height_feet !== '' ? Number(athleteProfile.height_feet) : null;
      aclCareData.height_inches = athleteProfile.height_inches !== '' ? Number(athleteProfile.height_inches) : null;
      aclCareData.weight_lbs = athleteProfile.weight_lbs !== '' ? Number(athleteProfile.weight_lbs) : null;
      aclCareData.primary_sport = athleteProfile.primary_sport.trim() || null;
      aclCareData.competition_level = athleteProfile.competition_level || null;
      aclCareData.medical_certification_accepted = true;
      aclCareData.medical_certification_accepted_at = now;

      if (isOrgLinked) {
        aclCareData.org_data_sharing_accepted = terms.org_data_sharing_accepted;
        if (terms.org_data_sharing_accepted) {
          aclCareData.org_data_sharing_accepted_at = now;
        }
      }

      const patientData = {
        first_name: personalInfo.first_name.trim(),
        last_name: personalInfo.last_name.trim(),
        phone: personalInfo.phone,
        birthday: personalInfo.birthday,
        address: {
          zip: zip.trim(),
          country: 'US',
        },
      };

      await api.updatePatient(user.patient_id, patientData);
    }

    userData.acl_care = aclCareData;

    const result = await api.updateUser(user.id, userData);

    // Only notify if marking complete
    if (result && markComplete) {
      try {
        await api.aclOnboardingComplete(user.id);
      } catch (notifyError) {
        console.error('Failed to notify onboarding complete:', notifyError);
      }
    }

    return result;
  };

  // Handle terms completion - either go to payment or finish onboarding
  const handleTermsComplete = async () => {
    if (!validateTerms()) return;

    setLoading(true);
    setError('');

    try {
      if (showPaymentStep) {
        // Save user data but DON'T mark onboarding complete yet
        await saveUserData(false);
        await refreshUser();
        // Go to payment step
        setStep(totalSteps);
      } else {
        // Org-linked or provider - save and mark complete
        await saveUserData(true);
        await refreshUser();
        navigate('/program', { replace: true });
      }
    } catch (err) {
      console.error('Onboarding error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Payment step handlers
  const handleValidateCode = async () => {
    if (!accessCode.trim()) {
      setCodeError('Please enter a code');
      return;
    }

    setCodeValidating(true);
    setCodeError('');
    setCodeResult(null);

    try {
      const result = await api.validateAccessCode(accessCode.trim(), user.id);

      if (result.valid) {
        setCodeResult(result);

        if (result.type === 'full_access') {
          if (result.add_org) {
            // Has org linkage - show consent checkbox, don't apply yet
            // User will click "Get Started" to proceed
          } else {
            // Regular full access - apply immediately
            await applyFullAccess(result, false);
          }
        }
        // If discount, just show success - user can proceed to checkout
      } else {
        setCodeError('Invalid code. Please check and try again.');
      }
    } catch (err) {
      console.error('Code validation error:', err);
      setCodeError('Failed to validate code. Please try again.');
    } finally {
      setCodeValidating(false);
    }
  };

  // Apply full access code by updating user's acl_care
  const applyFullAccess = async (codeData, orgConsent) => {
    const now = new Date().toISOString();

    const updatedAclCare = {
      ...user?.acl_care,
      access_status: 'access_code',
      access_code_used: accessCode.trim().toLowerCase(),
      access_granted_at: now,
      acl_onboarding_completed: true,
      acl_onboarding_completed_at: now,
    };

    // Add org consent if this is an add_org code
    if (codeData.add_org) {
      updatedAclCare.org_data_sharing_accepted = orgConsent;
      if (orgConsent) {
        updatedAclCare.org_data_sharing_accepted_at = now;
      }
    }

    // Build user update - include organization_id if add_org
    const userUpdate = {
      acl_care: updatedAclCare,
    };

    if (codeData.add_org && codeData.org_id) {
      userUpdate.organization_id = codeData.org_id;
    }

    await api.updateUser(user.id, userUpdate);

    // Notify onboarding complete
    try {
      await api.aclOnboardingComplete(user.id);
    } catch (notifyError) {
      console.error('Failed to notify onboarding complete:', notifyError);
    }

    await refreshUser();
    navigate('/program', { replace: true });
  };

  // Handle confirming full access code with org consent
  const handleConfirmFullAccess = async () => {
    setCodeValidating(true);
    setCodeError('');

    try {
      await applyFullAccess(codeResult, paymentOrgConsent);
    } catch (err) {
      console.error('Code application error:', err);
      setCodeError('Failed to apply code. Please try again.');
      setCodeValidating(false);
    }
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    setError('');

    try {
      const discountCode = codeResult?.type === 'discount' ? accessCode.trim() : null;
      const result = await api.createCheckoutSession(user.id, user.email, discountCode);

      if (result.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkout_url;
      } else {
        setError('Failed to create checkout session. Please try again.');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleStartTrial = async () => {
    setTrialLoading(true);
    setError('');

    try {
      const result = await api.startTrial(user.id);

      if (result.success) {
        await refreshUser();
        navigate('/program', { replace: true });
      } else {
        setError(result.message || 'Failed to start trial. Please try again.');
      }
    } catch (err) {
      console.error('Trial error:', err);
      setError('Failed to start trial. Please try again.');
    } finally {
      setTrialLoading(false);
    }
  };

  const clearCodeInput = () => {
    setAccessCode('');
    setCodeResult(null);
    setCodeError('');
    setPaymentOrgConsent(false);
  };

  // Get step info for progress indicator
  const getStepInfo = () => {
    if (!isPatient) {
      return [
        { icon: User, label: 'Personal Info' },
        { icon: FileText, label: 'Terms' },
      ];
    }

    const steps = [
      { icon: User, label: 'Personal Info' },
      { icon: Calendar, label: 'Injury Details' },
      { icon: FileText, label: 'Terms' },
    ];

    if (showPaymentStep) {
      steps.push({ icon: CreditCard, label: 'Payment' });
    }

    return steps;
  };

  const stepInfo = getStepInfo();

  // Render personal info step
  const renderPersonalInfoStep = () => (
    <div className="space-y-6">
      {/* Legacy user banner */}
      {isLegacyUser && showLegacyBanner && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 relative">
          <button
            onClick={() => setShowLegacyBanner(false)}
            className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
          <p className="text-sm text-blue-800 pr-6">
            We've launched a new version of the app. You'll need to complete a short onboarding process and accept terms. Thank you!
          </p>
        </div>
      )}

      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
        <p className="text-gray-600 mt-1">Tell us a bit about yourself</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="First Name"
          type="text"
          value={personalInfo.first_name}
          onChange={(e) => updatePersonalInfo('first_name', e.target.value)}
          required
        />
        <Input
          label="Last Name"
          type="text"
          value={personalInfo.last_name}
          onChange={(e) => updatePersonalInfo('last_name', e.target.value)}
          required
        />
      </div>

      <Input
        label="Email"
        type="email"
        value={personalInfo.email}
        disabled
        hint="Email cannot be changed"
      />

      <Input
        label="Phone Number"
        type="tel"
        value={personalInfo.phone}
        onChange={handlePhoneChange}
        placeholder="5551234567"
        hint="10 digits, no dashes or spaces"
        required
      />

      <Input
        label="Birthday"
        type="date"
        value={personalInfo.birthday}
        onChange={(e) => updatePersonalInfo('birthday', e.target.value)}
        required
      />

      {isPatient && (
        <Input
          label="ZIP Code"
          type="text"
          value={zip}
          onChange={(e) => {
            setZip(e.target.value.replace(/\D/g, '').slice(0, 5));
            setError('');
          }}
          placeholder="12345"
          maxLength={5}
        />
      )}

      <div className="pt-2">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Communication Preferences</h3>

        <div className="space-y-3">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={personalInfo.sms_preferences}
              onChange={(e) => updatePersonalInfo('sms_preferences', e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700">
              I'd like to receive SMS notifications
            </span>
          </label>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={personalInfo.email_preferences}
              onChange={(e) => updatePersonalInfo('email_preferences', e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700">
              I'd like to receive email notifications
            </span>
          </label>
        </div>
      </div>

      {isPatient && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sex
            </label>
            <select
              value={athleteProfile.sex}
              onChange={(e) => updateAthleteProfile('sex', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Select</option>
              {SEX_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Height
            </label>
            <div className="grid grid-cols-2 gap-3">
              <select
                value={athleteProfile.height_feet}
                onChange={(e) => updateAthleteProfile('height_feet', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Feet</option>
                {[3, 4, 5, 6, 7].map((ft) => (
                  <option key={ft} value={ft}>{ft} ft</option>
                ))}
              </select>
              <select
                value={athleteProfile.height_inches}
                onChange={(e) => updateAthleteProfile('height_inches', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Inches</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i} value={i}>{i} in</option>
                ))}
              </select>
            </div>
          </div>

          <Input
            label="Weight (lbs)"
            type="number"
            inputMode="numeric"
            min="1"
            max="999"
            value={athleteProfile.weight_lbs}
            onChange={(e) => updateAthleteProfile('weight_lbs', e.target.value)}
            placeholder="150"
            required
          />

          <Input
            label="Primary Sport"
            type="text"
            value={athleteProfile.primary_sport}
            onChange={(e) => updateAthleteProfile('primary_sport', e.target.value)}
            placeholder="Soccer"
            hint="Enter N/A if no sport"
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Competition Level
            </label>
            <select
              value={athleteProfile.competition_level}
              onChange={(e) => updateAthleteProfile('competition_level', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Select</option>
              {COMPETITION_LEVELS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="pt-4">
        <Button onClick={handleNext} fullWidth>
          Continue
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  // Render injury details step (patients only)
  const renderInjuryDetailsStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Injury Details</h2>
        <p className="text-gray-600 mt-1">This helps us personalize your recovery program</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Which knee was injured?
        </label>
        <div className="grid grid-cols-2 gap-3">
          {['left', 'right'].map((side) => (
            <button
              key={side}
              type="button"
              onClick={() => updateInjuryDetails('injury_side', side)}
              className={`
                py-3 px-4 rounded-lg border-2 text-center font-medium transition-colors
                ${injuryDetails.injury_side === side
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }
              `}
            >
              {side.charAt(0).toUpperCase() + side.slice(1)} Knee
            </button>
          ))}
        </div>
      </div>

      <Input
        label="Date of ACL Injury"
        type="date"
        value={injuryDetails.injury_date}
        onChange={(e) => updateInjuryDetails('injury_date', e.target.value)}
        hint="Approximate date if you're not sure. Enter 09/09/9999 if you have not had an ACL injury."
        required
      />

      <Input
        label="Date of Surgery"
        type="date"
        value={injuryDetails.surgery_date}
        onChange={(e) => updateInjuryDetails('surgery_date', e.target.value)}
        hint="Enter 09/09/9999 if you have not had surgery"
        required
      />

      {injuryDetails.surgery_date !== NO_SURGERY_DATE && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Surgical Graft Type
            </label>
            <select
              value={injuryDetails.graft_type}
              onChange={(e) => updateInjuryDetails('graft_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Select</option>
              {GRAFT_TYPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estimated Number of Post-Operative Physical Therapy Visits Completed
            </label>
            <select
              value={injuryDetails.pt_visits_completed}
              onChange={(e) => updateInjuryDetails('pt_visits_completed', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Select</option>
              {Array.from({ length: 99 }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </div>
        </>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Have you had a prior ACL tear before the current one?
        </label>
        <select
          value={injuryDetails.prior_acl_tear}
          onChange={(e) => updateInjuryDetails('prior_acl_tear', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="">Select</option>
          {PRIOR_ACL_TEAR_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3 pt-4">
        <Button variant="outline" onClick={handleBack}>
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={handleNext} fullWidth>
          Continue
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  // Render terms step
  const renderTermsStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Terms & Agreements</h2>
        <p className="text-gray-600 mt-1">Please review and accept to continue</p>
      </div>

      <div className="space-y-4">
        <label className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <input
            type="checkbox"
            checked={terms.general_accepted}
            onChange={(e) => setTerms(prev => ({ ...prev, general_accepted: e.target.checked }))}
            className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <div>
            <span className="text-sm text-gray-900 font-medium">
              I have read and agree to the{' '}
              <a
                href="https://aclcarepro.com/terms-of-use/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline hover:no-underline"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Use
              </a>
              {' '}and{' '}
              <a
                href="https://aclcarepro.com/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline hover:no-underline"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </a>
            </span>
          </div>
        </label>

        {isPatient && (
          <label className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={terms.medical_certification_accepted}
              onChange={(e) => setTerms(prev => ({ ...prev, medical_certification_accepted: e.target.checked }))}
              className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <div>
              <span className="text-sm text-gray-900 font-medium block mb-1">
                Medical Certification
              </span>
              <span className="text-sm text-gray-600">
                Please certify that the following statement is true. If this statement is false,
                please refer back to your physical therapist or surgeon and our terms of use and
                confirm that you are appropriate for this program.
              </span>
              <span className="text-sm text-gray-700 block mt-2 italic">
                "I have no pain with the activities of daily living (stair climbing, walking, sitting, squatting),
                have completed a course of supervised physical therapy post surgery, and have been cleared by my
                surgeon to perform jumping and running activities."
              </span>
            </div>
          </label>
        )}
        {/* Removing this for now
        {isPatient && isOrgLinked && orgName && (
          <label className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={terms.org_data_sharing_accepted}
              onChange={(e) => setTerms(prev => ({ ...prev, org_data_sharing_accepted: e.target.checked }))}
              className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <div>
              <span className="text-sm text-gray-900 font-medium block mb-1">
                Data Sharing with {orgName}
                <span className="text-gray-500 font-normal ml-1">(Optional)</span>
              </span>
              <span className="text-sm text-gray-600">
                You were referred by {orgName}. If you check this box, {orgName} will be able to view
                your data and progress. This is optional and you can still use ACL Care without sharing your data.
              </span>
            </div>
          </label>
        )}
        */}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3 pt-4">
        <Button variant="outline" onClick={handleBack} disabled={loading}>
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={handleTermsComplete} fullWidth loading={loading}>
          {showPaymentStep ? 'Continue' : 'Get Started'}
          {showPaymentStep && <ChevronRight className="ml-2 h-4 w-4" />}
        </Button>
      </div>
    </div>
  );

  // Render payment step
  const renderPaymentStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Unlock Your Program</h2>
        <p className="text-gray-600 mt-1">Choose how you'd like to get started</p>
      </div>

      {/* Access/Discount Code Section */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Tag className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Have an access or discount code?</span>
        </div>

        {codeResult?.type === 'full_access' ? (
          codeResult?.add_org ? (
            // Full access with org linkage - show consent checkbox
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Access code valid!</span>
              </div>
              {/*
              <label className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={paymentOrgConsent}
                  onChange={(e) => setPaymentOrgConsent(e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <div>
                  <span className="text-sm text-gray-900 font-medium block mb-1">
                    Data Sharing with {codeResult.org_name}
                    <span className="text-gray-500 font-normal ml-1">(Optional)</span>
                  </span>
                  <span className="text-sm text-gray-600">
                    You were referred by {codeResult.org_name}. If you check this box, {codeResult.org_name} will be able to view
                    your data and progress. This is optional.
                  </span>
                </div>
              </label>
              */}

              <Button
                onClick={handleConfirmFullAccess}
                fullWidth
                loading={codeValidating}
              >
                Get Started
              </Button>

              <button
                onClick={clearCodeInput}
                className="w-full text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Use a different code
              </button>
            </div>
          ) : (
            // Regular full access - should have already navigated, but just in case
            <div className="flex items-center gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-medium">Full access granted!</span>
            </div>
          )
        ) : codeResult?.type === 'discount' ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-medium">Discount code applied!</span>
            </div>
            <button
              onClick={clearCodeInput}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Remove code
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="text"
                value={accessCode}
                onChange={(e) => {
                  setAccessCode(e.target.value.toUpperCase());
                  setCodeError('');
                }}
                placeholder="Enter code"
                error={codeError}
              />
            </div>
            <Button
              onClick={handleValidateCode}
              disabled={codeValidating || !accessCode.trim()}
              variant="outline"
              className="shrink-0"
            >
              {codeValidating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Apply'
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">
            {codeResult?.type === 'discount' ? 'Continue with discount' : 'or'}
          </span>
        </div>
      </div>

      {/* Payment Options */}
      <div className="space-y-4">
        {/* Pay Now Button */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h3 className="font-semibold text-gray-900">Program Access</h3>
              <p className="text-sm text-gray-500">One-time payment</p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-gray-900">${PROGRAM_PRICE}</span>
            </div>
          </div>

          <Button
            onClick={handleCheckout}
            fullWidth
            loading={checkoutLoading}
            disabled={checkoutLoading || trialLoading}
          >
            <CreditCard className="mr-2 h-4 w-4" />
            Pay Now
          </Button>

          <p className="text-xs text-gray-500 text-center mt-2">
            Klarna & Afterpay available at checkout for installment payments
          </p>
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">or</span>
          </div>
        </div>

        {/* Free Trial */}
        <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-4">
          <div className="text-center mb-3">
            <h3 className="font-semibold text-gray-900">Start with a Free Trial</h3>
            <p className="text-sm text-gray-600">7 days of full access, no credit card required</p>
          </div>

          <Button
            onClick={handleStartTrial}
            variant="outline"
            fullWidth
            loading={trialLoading}
            disabled={checkoutLoading || trialLoading}
          >
            Start 7-Day Free Trial
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="pt-2">
        <Button variant="outline" onClick={handleBack} disabled={checkoutLoading || trialLoading}>
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </div>
    </div>
  );

  // Render current step content
  const renderStepContent = () => {
    if (isPatient) {
      switch (step) {
        case 1: return renderPersonalInfoStep();
        case 2: return renderInjuryDetailsStep();
        case 3: return renderTermsStep();
        case 4: return renderPaymentStep();
        default: return null;
      }
    } else {
      switch (step) {
        case 1: return renderPersonalInfoStep();
        case 2: return renderTermsStep();
        default: return null;
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-8">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white py-8 px-6 shadow-md rounded-lg">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Activity className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome to ACL Care</h1>
          </div>

          {/* Progress indicator */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center">
              {stepInfo.map((info, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center transition-colors
                      ${index + 1 < step
                        ? 'bg-primary text-white'
                        : index + 1 === step
                          ? 'bg-primary text-white'
                          : 'bg-gray-200 text-gray-500'
                      }
                    `}
                  >
                    {index + 1 < step ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <info.icon className="h-4 w-4" />
                    )}
                  </div>
                  {index < stepInfo.length - 1 && (
                    <div
                      className={`
                        w-8 h-1 mx-1 rounded transition-colors
                        ${index + 1 < step ? 'bg-primary' : 'bg-gray-200'}
                      `}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
}